import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import {
  putLocalSurvey, getLocalSurvey, enqueueOp, putLocalPhoto, getLocalPhoto, removeLocalPhoto,
  clientId as newClientId, isOnline, scheduleSync, listLocalSurveys,
} from "../lib/surveysOffline";
import { ArrowLeft, Save, Camera, X, Send, Loader2 } from "lucide-react";
import { toast } from "../components/ui/sonner";
import SignatureCanvas from "../components/SignatureCanvas";
import BarcodeScanner from "../components/BarcodeScanner";
import { CustomerAutocomplete } from "../components/quote-builder/CustomerAutocomplete";
import { SiteAutocomplete } from "../components/quote-builder/SiteAutocomplete";

/**
 * SurveyBuilderPage — fills out a survey from a template, online or offline.
 * Local-first: every change is persisted to IndexedDB immediately, then a
 * sync op is enqueued. When online, the queue auto-drains.
 *
 * URL params:
 *   - template=<tid>   (new survey) — required when no cid loaded
 *   - cid=<client_id>  (resume a local draft)
 *   - sid=<server_id>  (server survey to pull and cache locally)
 */
export default function SurveyBuilderPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialTemplateId = params.get("template") || "";
  const urlCid = params.get("cid") || "";
  const urlSid = params.get("sid") || "";

  const [template, setTemplate] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [hsQuestions, setHsQuestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState(false);
  const initRan = useRef(false);

  // Fetch the H&S questionnaire once (single doc; if we're offline this may
  // fail — the H&S section then simply doesn't render).
  useEffect(() => {
    api.get("/hs-questionnaire")
      .then((r) => setHsQuestions((r.data?.questions || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0))))
      .catch(() => {});
  }, []);

  // Bootstrap: load template + either a local draft (cid) or pull server (sid) or create new
  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;
    (async () => {
      let cid = urlCid;
      let local = cid ? await getLocalSurvey(cid) : null;
      let tid = initialTemplateId || local?.template_id;

      // If only sid is passed, see if we already have a local copy
      if (!local && urlSid) {
        const all = await listLocalSurveys();
        local = all.find((s) => s.server_id === urlSid) || null;
        if (local) cid = local.client_id;
      }

      // No local draft yet → pull from server or create new
      if (!local) {
        if (urlSid) {
          try {
            const { data } = await api.get(`/surveys/${urlSid}`);
            cid = newClientId();
            local = {
              client_id: cid,
              server_id: data.id,
              template_id: data.template_id,
              template_name: data.template_name,
              product_type: data.product_type,
              customer_name: data.customer_name || "",
              site_name: data.site_name || "",
              site_address: data.site_address || "",
              postcode: data.postcode || "",
              answers: data.answers || {},
              photo_ids: data.photo_ids || {},
              signatures: data.signatures || {},
              notes: data.notes || "",
              status: data.status || "Draft",
              created_at: data.created_at,
            };
            tid = data.template_id;
            await putLocalSurvey(local);
          } catch (_) {
            toast.error("Couldn't load survey.");
            nav("/surveys");
            return;
          }
        } else if (tid) {
          cid = cid || newClientId();
          local = {
            client_id: cid,
            template_id: tid,
            customer_name: "",
            site_name: "",
            site_address: "",
            postcode: "",
            answers: {},
            photo_ids: {},
            signatures: {},
            notes: "",
            status: "Draft",
            created_at: new Date().toISOString(),
          };
          await putLocalSurvey(local);
        } else {
          toast.error("Pick a template first.");
          nav("/surveys");
          return;
        }
      }

      // Load template (locally, fallback to server)
      let tpl = null;
      try {
        const { data } = await api.get(`/survey-templates/${tid}`);
        tpl = data;
      } catch (_) { /* offline / 404 / permission */ }
      if (!tpl && local.template_snapshot) tpl = local.template_snapshot;
      if (!tpl) {
        // Template no longer exists AND we have no cached snapshot → the
        // page would otherwise hang on "Loading survey…" forever. Bail out
        // and send the user back to the surveys list with a clear toast so
        // they can pick a different survey (or ask an admin to restore the
        // template).
        toast.error("This survey's template is no longer available. Please pick a live template from Surveys.");
        nav("/surveys");
        return;
      }
      // Snapshot the template inside the local draft so future offline edits still know the questions
      await putLocalSurvey({ ...local, template_snapshot: tpl, template_name: tpl.name, product_type: tpl.product_type });
      local = { ...local, template_snapshot: tpl, template_name: tpl.name, product_type: tpl.product_type };
      setTemplate(tpl);
      setSurvey(local);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (overrides = {}, { sync = true } = {}) => {
    if (!survey) return;
    setBusy(true);
    const merged = { ...survey, ...overrides };
    await putLocalSurvey(merged);
    setSurvey(merged);
    if (sync) {
      if (!merged.server_id) {
        await enqueueOp({ type: "create_survey", client_id: merged.client_id });
      } else {
        await enqueueOp({ type: "update_survey", client_id: merged.client_id });
      }
      if (isOnline()) scheduleSync(150);
    }
    setBusy(false);
  };

  const setField = (k, v) => save({ [k]: v });
  const setAnswer = (qid, v) => save({ answers: { ...(survey?.answers || {}), [qid]: v } });
  const setHsAnswer = (qid, v) => save({ hs_answers: { ...(survey?.hs_answers || {}), [qid]: v } });

  const addPhoto = async (qid, file) => {
    if (!file || !survey) return;
    const id = "local-" + newClientId();
    await putLocalPhoto(id, file, { filename: file.name, type: file.type });
    const next = { ...(survey.photo_ids || {}) };
    next[qid] = [...(next[qid] || []), id];
    await save({ photo_ids: next }, { sync: false });
    await enqueueOp({ type: "upload_photo", client_id: survey.client_id, photo_id: id, question_id: qid });
    if (isOnline()) scheduleSync(200);
  };

  const removePhoto = async (qid, photoId) => {
    if (!survey) return;
    const next = { ...(survey.photo_ids || {}) };
    next[qid] = (next[qid] || []).filter((p) => p !== photoId);
    if (photoId.startsWith("local-")) {
      await removeLocalPhoto(photoId);
    } else if (survey.server_id) {
      try { await api.delete(`/surveys/${survey.server_id}/photos/${photoId}`); } catch (_) { /* ignore */ }
    }
    await save({ photo_ids: next });
  };

  const markCompleted = async () => {
    await save({ status: "Completed" });
    toast.success("Survey marked as completed.");
  };

  const convertToQuote = async () => {
    setConverting(true);
    try {
      // If the local draft hasn't synced yet, drain the queue first so the
      // server knows about the survey before we ask it to convert.
      let live = survey;
      if (!live?.server_id) {
        toast.info("Syncing draft before converting…");
        const { syncNow } = await import("../lib/surveysOffline");
        for (let i = 0; i < 6; i++) {
          await syncNow();
          live = await getLocalSurvey(survey.client_id);
          if (live?.server_id) break;
          await new Promise((r) => setTimeout(r, 500));
        }
        if (live?.server_id) setSurvey(live);
      }
      if (!live?.server_id) {
        toast.error("Couldn't sync to server — check connectivity and try again.");
        return;
      }
      const { data } = await api.post(`/surveys/${live.server_id}/convert-to-quote`);
      await save({ status: "Converted" }, { sync: false });
      toast.success(`Quote ${data.quote_number} created from this survey.`);
      nav(`/quotes/${data.quote_id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed.");
    } finally {
      setConverting(false);
    }
  };

  if (!survey || !template) {
    return <div className="p-10 text-slate-400">Loading survey…</div>;
  }

  const questions = (template.questions || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  // Shared branching walk — applies the same "jump to target_id" logic to any
  // ordered question array + answers dict. Reused for both the product
  // questions and the trailing Health & Safety section.
  const applyBranching = (qs, answers) => {
    const visible = [];
    const idToIdx = new Map(qs.map((q, idx) => [q.id, idx]));
    let i = 0;
    while (i < qs.length) {
      const q = qs[i];
      visible.push(q);
      let nextIdx = i + 1;
      const rules = q.branching_rules || [];
      if (rules.length > 0) {
        const ans = answers[q.id];
        let candidate = null;
        if (q.type === "yes_no") {
          const key = ans === true ? "Yes" : ans === false ? "No" : null;
          if (key) candidate = rules.find((r) => (r.value || "").toLowerCase() === key.toLowerCase());
        } else if (q.type === "dropdown") {
          if (ans) candidate = rules.find((r) => r.value === ans);
        } else if (q.type === "multi_select" && Array.isArray(ans)) {
          candidate = rules.find((r) => ans.includes(r.value));
        }
        if (candidate && candidate.target_id && idToIdx.has(candidate.target_id)) {
          const tgt = idToIdx.get(candidate.target_id);
          if (tgt > i) nextIdx = tgt;
        }
      }
      i = nextIdx;
    }
    return visible;
  };

  const visibleQuestions = applyBranching(questions, survey.answers || {});
  const visibleHsQuestions = applyBranching(hsQuestions, survey.hs_answers || {});

  return (
    <div className="space-y-5 fade-up" data-testid="survey-builder">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => nav("/surveys")} className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="back-to-surveys">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {survey.status === "Completed" && (
            <button
              onClick={convertToQuote}
              disabled={converting}
              data-testid="convert-to-quote"
              className="h-10 px-5 bg-brand-orange text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50"
            >
              {converting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Convert to Quote
            </button>
          )}
          {survey.status !== "Completed" && survey.status !== "Converted" && (
            <button
              onClick={markCompleted}
              data-testid="mark-completed"
              disabled={busy}
              className="h-10 px-5 bg-blue-600 text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-blue-700"
            >
              <Save size={14} /> Mark completed
            </button>
          )}
        </div>
      </div>

      <div className="qb-card p-5 space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{survey.product_type}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">{template.name}</h1>
          </div>
          <div className="text-xs text-slate-500">
            {survey.server_id ? <span data-testid="server-id">Server #{survey.server_id.slice(0, 8)}</span> : <span className="text-amber-600">Local draft — not yet synced</span>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Customer Name">
            <CustomerAutocomplete
              value={survey.customer_name}
              onChange={(name) => setField("customer_name", name)}
            />
          </Field>
          <Field label="Site Name">
            <SiteAutocomplete
              customerName={survey.customer_name}
              value={survey.site_name}
              onChange={(v) => setField("site_name", v)}
              onPick={(s) => save({
                site_name: s.site_name || "",
                site_address: s.site_address || survey.site_address || "",
                postcode: s.postcode || survey.postcode || "",
              })}
              testid="survey-site-name"
            />
          </Field>
          <Field label="Site Address">
            <input data-testid="survey-address" className="qb-input" value={survey.site_address} onChange={(e) => setField("site_address", e.target.value)} />
          </Field>
          <Field label="Postcode">
            <input data-testid="survey-postcode" className="qb-input" value={survey.postcode} onChange={(e) => setField("postcode", e.target.value.toUpperCase())} />
          </Field>
        </div>
      </div>

      <div className="qb-card p-5 space-y-5">
        {questions.length === 0 && <div className="text-slate-500">No questions defined for this template.</div>}
        {visibleQuestions.map((q, idx) => (
          <QuestionRow
            key={q.id || idx}
            q={q}
            value={survey.answers?.[q.id]}
            signature={survey.signatures?.[q.id]}
            photoIds={survey.photo_ids?.[q.id] || []}
            onChange={(v) => setAnswer(q.id, v)}
            onSignature={(dataUrl) => save({ signatures: { ...(survey.signatures || {}), [q.id]: dataUrl } })}
            onAddPhoto={(file) => addPhoto(q.id, file)}
            onRemovePhoto={(pid) => removePhoto(q.id, pid)}
            disabled={survey.status === "Converted"}
          />
        ))}
      </div>

      {/* Health & Safety questionnaire — appended after every survey. */}
      {hsQuestions.length > 0 && (
        <>
          <div className="qb-card p-5 border-l-4 border-brand-orange bg-orange-50/40 flex items-start gap-3" data-testid="hs-intro">
            <div className="shrink-0 w-8 h-8 rounded-full bg-brand-orange text-white inline-flex items-center justify-center text-sm font-bold">!</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-orange font-bold">Next section</div>
              <h2 className="font-display text-lg font-black tracking-tight mt-0.5">
                You will now need to complete a Health &amp; Safety Questionnaire
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Answer every question below. Skipped questions may hide follow-ups based on your answers.
              </p>
            </div>
          </div>

          <div className="qb-card p-5 space-y-5" data-testid="hs-section">
            {visibleHsQuestions.map((q, idx) => (
              <QuestionRow
                key={`hs-${q.id || idx}`}
                q={q}
                value={survey.hs_answers?.[q.id]}
                signature={survey.hs_signatures?.[q.id]}
                photoIds={[]}
                onChange={(v) => setHsAnswer(q.id, v)}
                onSignature={(dataUrl) => save({ hs_signatures: { ...(survey.hs_signatures || {}), [q.id]: dataUrl } })}
                onAddPhoto={() => {}}
                onRemovePhoto={() => {}}
                disabled={survey.status === "Converted"}
              />
            ))}
          </div>
        </>
      )}

      <div className="qb-card p-5">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Additional notes</label>
        <textarea
          data-testid="survey-notes"
          value={survey.notes || ""}
          onChange={(e) => setField("notes", e.target.value)}
          className="qb-input min-h-[96px] mt-1"
          placeholder="Anything else worth recording about the site…"
        />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}

function QuestionRow({ q, value, signature, photoIds, onChange, onSignature, onAddPhoto, onRemovePhoto, disabled }) {
  return (
    <div className="space-y-2" data-testid={`question-${q.id}`}>
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-sm font-semibold text-slate-900">
          {q.label} {q.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">{q.type}</span>
      </div>
      {q.help_text && <p className="text-xs text-slate-500">{q.help_text}</p>}
      <AnswerInput q={q} value={value} signature={signature} photoIds={photoIds}
        onChange={onChange} onSignature={onSignature} onAddPhoto={onAddPhoto} onRemovePhoto={onRemovePhoto}
        disabled={disabled}
      />
    </div>
  );
}

function AnswerInput({ q, value, signature, photoIds, onChange, onSignature, onAddPhoto, onRemovePhoto, disabled }) {
  if (disabled && q.type !== "photo" && q.type !== "signature") {
    return <div className="text-sm text-slate-600">{String(value ?? "—")}</div>;
  }
  switch (q.type) {
    case "short_text":
      return <input className="qb-input" placeholder={q.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`answer-${q.id}`} />;
    case "long_text":
      return <textarea className="qb-input min-h-[80px]" placeholder={q.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`answer-${q.id}`} />;
    case "number":
      return <input type="number" className="qb-input" placeholder={q.placeholder} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={`answer-${q.id}`} />;
    case "date":
      return <input type="date" className="qb-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`answer-${q.id}`} />;
    case "yes_no":
      return (
        <div className="inline-flex border border-slate-300 rounded-full overflow-hidden">
          {["Yes", "No"].map((opt) => (
            <button key={opt} type="button"
              onClick={() => onChange(opt === "Yes")}
              className={`px-4 h-9 text-xs uppercase tracking-wider font-semibold ${
                (opt === "Yes" && value === true) || (opt === "No" && value === false)
                  ? "bg-brand-blue text-white" : "text-slate-600"
              }`}
              data-testid={`answer-${q.id}-${opt.toLowerCase()}`}
            >{opt}</button>
          ))}
        </div>
      );
    case "dropdown":
      return (
        <select className="qb-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`answer-${q.id}`}>
          <option value="">— select —</option>
          {(q.options || []).map((o) => <option key={o}>{o}</option>)}
        </select>
      );
    case "multi_select": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) => {
        const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
        onChange(next);
      };
      return (
        <div className="flex flex-wrap gap-2">
          {(q.options || []).map((opt) => (
            <button key={opt} type="button" onClick={() => toggle(opt)}
              className={`px-3 h-8 rounded-full text-xs font-semibold border ${
                arr.includes(opt) ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-slate-600 border-slate-300 hover:border-brand-blue"
              }`}
              data-testid={`answer-${q.id}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            >{opt}</button>
          ))}
        </div>
      );
    }
    case "measurement": {
      const v = value || {};
      return (
        <div className="grid grid-cols-4 gap-2">
          {[["w", "Width"], ["h", "Height"], ["d", "Depth"]].map(([k, lbl]) => (
            <label key={k} className="block">
              <span className="block text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{lbl}</span>
              <input type="number" className="qb-input" value={v[k] ?? ""} onChange={(e) => onChange({ ...v, [k]: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`answer-${q.id}-${k}`} />
            </label>
          ))}
          <label className="block">
            <span className="block text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Unit</span>
            <select className="qb-input" value={v.unit || "mm"} onChange={(e) => onChange({ ...v, unit: e.target.value })}>
              <option>mm</option><option>cm</option><option>m</option>
            </select>
          </label>
        </div>
      );
    }
    case "photo":
      return <PhotoAnswer q={q} photoIds={photoIds} onAdd={onAddPhoto} onRemove={onRemovePhoto} disabled={disabled} />;
    case "signature":
      return <SignatureCanvas value={signature} onChange={onSignature} disabled={disabled} testid={`answer-${q.id}`} />;
    case "barcode":
      return <BarcodeScanner value={value || ""} onChange={onChange} disabled={disabled} testid={`answer-${q.id}`} />;
    default:
      return <div className="text-sm text-amber-600">Unknown question type: {q.type}</div>;
  }
}

function PhotoAnswer({ q, photoIds, onAdd, onRemove, disabled }) {
  const [previews, setPreviews] = useState({});
  // Resolve preview URLs (for local-prefixed ids we load the Blob from IDB)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const id of photoIds || []) {
        if (id.startsWith("local-")) {
          const p = await getLocalPhoto(id);
          if (p?.blob) next[id] = URL.createObjectURL(p.blob);
        } else {
          next[id] = `/api/files/${id}`;
        }
      }
      if (!cancelled) setPreviews(next);
    })();
    return () => {
      cancelled = true;
      Object.values(previews).forEach((u) => { if (u.startsWith("blob:")) URL.revokeObjectURL(u); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoIds.join(",")]);

  const inputRef = useRef(null);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {(photoIds || []).map((id) => (
          <div key={id} className="relative group border border-slate-200 rounded-md overflow-hidden bg-slate-50">
            {previews[id]
              ? <img src={previews[id]} alt="" className="w-full h-24 object-cover" />
              : <div className="w-full h-24 flex items-center justify-center text-slate-400 text-xs">Loading…</div>
            }
            {id.startsWith("local-") && (
              <span className="absolute top-1 left-1 text-[9px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-semibold uppercase tracking-wider">Pending</span>
            )}
            {!disabled && (
              <button onClick={() => onRemove(id)} className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-1" title="Remove"><X size={11} /></button>
            )}
          </div>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 hover:border-brand-blue text-slate-500 hover:text-brand-blue rounded-md flex flex-col items-center justify-center gap-1 h-24 text-xs font-semibold"
            data-testid={`add-photo-${q.id}`}
          >
            <Camera size={18} /> Add photo
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          files.forEach((f) => onAdd(f));
          e.target.value = "";
        }}
      />
    </div>
  );
}
